import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import { USER_ROLES } from '../config/security'
import ConfirmModal from './ConfirmModal'
import {
  desconectarGoogleCalendar,
  iniciarConexaoGoogleCalendar,
  sincronizarEventoGoogleCalendar,
  statusGoogleCalendar
} from '../services/googleCalendarApi'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './Calendario.css'

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

function startOfDay(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function endOfDay(d) {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

function startOfWeek(d) {
  const x = new Date(d)
  const day = x.getDay()
  const off = day === 0 ? 6 : day - 1
  x.setDate(x.getDate() - off)
  x.setHours(0, 0, 0, 0)
  return x
}

function endOfWeek(d) {
  const x = new Date(startOfWeek(d))
  x.setDate(x.getDate() + 6)
  x.setHours(23, 59, 59, 999)
  return x
}

function startOfMonth(d) {
  const x = new Date(d)
  x.setDate(1)
  x.setHours(0, 0, 0, 0)
  return x
}

function endOfMonth(d) {
  const x = new Date(d)
  x.setMonth(x.getMonth() + 1)
  x.setDate(0)
  x.setHours(23, 59, 59, 999)
  return x
}

function eventInRange(ev, start, end) {
  const t = new Date(ev.data_hora).getTime()
  return t >= start.getTime() && t <= end.getTime()
}

const Calendario = ({ onBack, userId, userName, userRole, userEmpresa }) => {
  const [eventos, setEventos] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(false)
  const [showFormModal, setShowFormModal] = useState(false)
  const [showDetalhesModal, setShowDetalhesModal] = useState(false)
  const [detalhesDia, setDetalhesDia] = useState(null)
  const [message, setMessage] = useState({ show: false, text: '', type: '' })
  const [showConflitoModal, setShowConflitoModal] = useState(false)
  const [showExcluirModal, setShowExcluirModal] = useState(false)
  const [eventoToExcluir, setEventoToExcluir] = useState(null)
  const [viewMode, setViewMode] = useState('dia')
  const [agendaDate, setAgendaDate] = useState(() => new Date())
  const [formData, setFormData] = useState({
    nome_evento: '',
    tipo_evento: 'vendas',
    data: '',
    hora: '',
    observacao: '',
    usuarios_ids: [],
    notificar_antecipacao_minutos: ''
  })
  const [usuariosSearch, setUsuariosSearch] = useState('')
  const [usuariosSearchFocused, setUsuariosSearchFocused] = useState(false)
  const [showConfigEmail, setShowConfigEmail] = useState(false)
  const [configEmail, setConfigEmail] = useState({ id: null, email_remetente: '', nome_remetente: 'Sistema Calendário' })
  const [configEmailSaving, setConfigEmailSaving] = useState(false)
  const [configEmailTesting, setConfigEmailTesting] = useState(false)
  const [editingEventoId, setEditingEventoId] = useState(null)
  const [formResponsavelNome, setFormResponsavelNome] = useState(null)
  const [showMenuCalendario, setShowMenuCalendario] = useState(false)
  const [googleAgenda, setGoogleAgenda] = useState({ conectado: false, google_email: null, loading: false })

  const loadUsuarios = useCallback(async () => {
    try {
      let query = supabase
        .from('usuarios_sistema')
        .select('id, nome, email')
        .eq('status', 'ativo')
        .order('nome')
      // Administrador vê todos; demais filtram por empresa
      if (userRole !== USER_ROLES.ADMINISTRADOR && userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }
      const { data, error } = await query
      if (error) throw error
      setUsuarios(data || [])
    } catch (err) {
      console.error('Erro ao carregar usuários:', err)
      showMsg('Erro ao carregar usuários', 'error')
    }
  }, [userRole, userEmpresa?.id])

  const carregarStatusGoogle = useCallback(async () => {
    if (!userId) return
    const res = await statusGoogleCalendar(userId)
    setGoogleAgenda({
      conectado: Boolean(res.conectado),
      google_email: res.google_email || null,
      loading: false
    })
  }, [userId])

  useEffect(() => {
    carregarStatusGoogle()
  }, [carregarStatusGoogle])

  useEffect(() => {
    const flag = sessionStorage.getItem('google_calendar_oauth')
    if (!flag) return
    sessionStorage.removeItem('google_calendar_oauth')
    if (flag === 'ok') {
      showMsg('Google Agenda conectada. Novos eventos serão copiados para a sua agenda.', 'success')
      carregarStatusGoogle()
    } else {
      showMsg('Não foi possível conectar a Google Agenda. Tente novamente.', 'error')
    }
  }, [carregarStatusGoogle])

  const loadEventos = useCallback(async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('eventos_calendario')
        .select(`
          id,
          nome_evento,
          tipo_evento,
          executado,
          data_hora,
          observacao,
          notificar_antecipacao_minutos,
          id_responsavel,
          responsavel:usuarios_sistema!id_responsavel(id, nome),
          eventos_calendario_usuarios(usuario_id, usuarios_sistema(id, nome))
        `)
        .order('data_hora', { ascending: true })

      const selectEventos = `
          id,
          nome_evento,
          tipo_evento,
          executado,
          data_hora,
          observacao,
          notificar_antecipacao_minutos,
          id_responsavel,
          responsavel:usuarios_sistema!id_responsavel(id, nome),
          eventos_calendario_usuarios(usuario_id, usuarios_sistema(id, nome))
        `
      // Somente administrador vê todos. Gerente, administrativo, gerente comercial e avulso veem eventos da empresa (via ordem_servico ou responsável).
      if (userRole === USER_ROLES.ADMINISTRADOR) {
        // Admin vê todos - nenhum filtro
      } else if ([USER_ROLES.GERENTE, USER_ROLES.ADMINISTRATIVO, USER_ROLES.GERENTE_COMERCIAL, USER_ROLES.AVULSO].includes(userRole) && userEmpresa?.id) {
        // Eventos da empresa: via id_ordem_servico -> ordens_servicos.id_empresa OU eventos sem ordem com responsável da empresa
        const { data: ordensEmpresa } = await supabase
          .from('ordens_servicos')
          .select('id')
          .eq('id_empresa', userEmpresa.id)
        const ordemIds = (ordensEmpresa || []).map(o => o.id).filter(Boolean)
        const { data: usuariosEmpresa } = await supabase
          .from('usuarios_sistema')
          .select('id')
          .eq('empresa_id', userEmpresa.id)
        const userIdsEmpresa = (usuariosEmpresa || []).map(u => u.id).filter(Boolean)

        const promises = []
        if (ordemIds.length > 0) {
          promises.push(
            supabase
              .from('eventos_calendario')
              .select(selectEventos)
              .in('id_ordem_servico', ordemIds)
              .order('data_hora', { ascending: true })
          )
        }
        if (userIdsEmpresa.length > 0) {
          promises.push(
            supabase
              .from('eventos_calendario')
              .select(selectEventos)
              .is('id_ordem_servico', null)
              .in('id_responsavel', userIdsEmpresa)
              .order('data_hora', { ascending: true })
          )
        }
        // Somente avulso: carregar vistorias onde o responsável é o usuário definido (todos os avulsos veem as vistorias desse usuário)
        if (userRole === USER_ROLES.AVULSO) {
          const ID_RESPONSAVEL_VISTORIAS_AVULSO = '7fc72870-0217-432e-8a71-947d3e141f44'
          const { data: tipoVistoria } = await supabase.from('tipo_servico').select('id').eq('nome', 'Vistoria').maybeSingle()
          if (tipoVistoria?.id) {
            const { data: respOrdens } = await supabase
              .from('ordem_servico_responsaveis')
              .select('id_ordem_servico')
              .eq('id_responsavel', ID_RESPONSAVEL_VISTORIAS_AVULSO)
            const ordemIdsResponsavel = [...new Set((respOrdens || []).map(r => r.id_ordem_servico).filter(Boolean))]
            if (ordemIdsResponsavel.length > 0) {
              const { data: ordensVistoria } = await supabase
                .from('ordens_servicos')
                .select('id')
                .eq('id_tipo_servico', tipoVistoria.id)
                .in('id', ordemIdsResponsavel)
              const vistoriaOrdemIds = (ordensVistoria || []).map(o => o.id).filter(Boolean)
              if (vistoriaOrdemIds.length > 0) {
                promises.push(
                  supabase
                    .from('eventos_calendario')
                    .select(selectEventos)
                    .in('id_ordem_servico', vistoriaOrdemIds)
                    .order('data_hora', { ascending: true })
                )
              }
            }
          }
        }
        if (promises.length === 0) {
          setEventos([])
          return
        }
        const results = await Promise.all(promises)
        const porId = new Map()
        results.forEach((res) => {
          if (res.data) res.data.forEach(e => { if (!porId.has(e.id)) porId.set(e.id, e) })
        })
        setEventos(Array.from(porId.values()).sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora)))
        return
      } else if (userId) {
        // Perfis como SDR: ver eventos onde é responsável OU onde foi adicionado em eventos_calendario_usuarios
        const { data: dataResp, error: errResp } = await supabase
          .from('eventos_calendario')
          .select(`
            id,
            nome_evento,
            tipo_evento,
            executado,
            data_hora,
            observacao,
            notificar_antecipacao_minutos,
            id_responsavel,
            responsavel:usuarios_sistema!id_responsavel(id, nome),
            eventos_calendario_usuarios(usuario_id, usuarios_sistema(id, nome))
          `)
          .eq('id_responsavel', userId)
          .order('data_hora', { ascending: true })
        if (errResp) throw errResp
        const { data: idsAssociados, error: errAssoc } = await supabase
          .from('eventos_calendario_usuarios')
          .select('evento_id')
          .eq('usuario_id', userId)
        if (errAssoc) throw errAssoc
        const eventoIdsAssociados = [...new Set((idsAssociados || []).map(r => r.evento_id).filter(Boolean))]
        if (eventoIdsAssociados.length === 0) {
          setEventos(dataResp || [])
          return
        }
        const { data: dataAssoc, error: errAssoc2 } = await supabase
          .from('eventos_calendario')
          .select(`
            id,
            nome_evento,
            tipo_evento,
            executado,
            data_hora,
            observacao,
            notificar_antecipacao_minutos,
            id_responsavel,
            responsavel:usuarios_sistema!id_responsavel(id, nome),
            eventos_calendario_usuarios(usuario_id, usuarios_sistema(id, nome))
          `)
          .in('id', eventoIdsAssociados)
          .order('data_hora', { ascending: true })
        if (errAssoc2) throw errAssoc2
        const porId = new Map((dataResp || []).map(e => [e.id, e]))
        ;(dataAssoc || []).forEach(e => { if (!porId.has(e.id)) porId.set(e.id, e) })
        setEventos(Array.from(porId.values()).sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora)))
        return
      }

      const { data, error } = await query
      if (error) throw error
      setEventos(data || [])
    } catch (err) {
      console.error('Erro ao carregar eventos:', err)
      showMsg('Erro ao carregar eventos', 'error')
    } finally {
      setLoading(false)
    }
  }, [userRole, userId, userEmpresa?.id])

  useEffect(() => {
    loadUsuarios()
    loadEventos()
  }, [loadUsuarios, loadEventos])

  const loadConfigEmail = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('config_notificacao_email')
        .select('id, email_remetente, nome_remetente')
        .eq('ativo', true)
        .limit(1)
        .maybeSingle()
      if (error) throw error
      if (data) {
        setConfigEmail({
          id: data.id,
          email_remetente: data.email_remetente || '',
          nome_remetente: data.nome_remetente || 'Sistema Calendário'
        })
      } else {
        setConfigEmail({ id: null, email_remetente: '', nome_remetente: 'Sistema Calendário' })
      }
    } catch (err) {
      console.error('Erro ao carregar config e-mail:', err)
    }
  }, [])

  const saveConfigEmail = async (e) => {
    e.preventDefault()
    const email = (configEmail.email_remetente || '').trim()
    if (!email) {
      showMsg('E-mail remetente é obrigatório.', 'error')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showMsg('E-mail remetente inválido.', 'error')
      return
    }
    try {
      setConfigEmailSaving(true)
      if (configEmail.id) {
        const { error } = await supabase
          .from('config_notificacao_email')
          .update({
            email_remetente: email,
            nome_remetente: (configEmail.nome_remetente || 'Sistema Calendário').trim()
          })
          .eq('id', configEmail.id)
        if (error) throw error
        showMsg('Configuração de e-mail atualizada.', 'success')
      } else {
        const { error } = await supabase
          .from('config_notificacao_email')
          .insert([{
            email_remetente: email,
            nome_remetente: (configEmail.nome_remetente || 'Sistema Calendário').trim(),
            ativo: true
          }])
        if (error) throw error
        showMsg('Configuração de e-mail gravada.', 'success')
        loadConfigEmail()
      }
      setShowConfigEmail(false)
    } catch (err) {
      console.error('Erro ao salvar config e-mail:', err)
      showMsg('Erro ao salvar: ' + (err.message || err), 'error')
    } finally {
      setConfigEmailSaving(false)
    }
  }

  const testarEnvioEmail = async () => {
    try {
      setConfigEmailTesting(true)
      const useProxy = process.env.NODE_ENV === 'development'
      let data, error
      if (useProxy) {
        const res = await fetch('/api/send-event-reminders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ test: true })
        })
        data = await res.json().catch(() => ({}))
        if (!res.ok) {
          error = { message: data?.error || res.statusText || 'Erro ao testar envio.' }
        }
      } else {
        const out = await supabase.functions.invoke('send-event-reminders', { body: { test: true } })
        data = out.data
        error = out.error
      }
      if (error) {
        const msg = data?.error
          ? (typeof data.error === 'string' ? data.error : data.error?.message)
          : (error?.message || 'Erro ao testar envio.')
        showMsg(msg, 'error')
        return
      }
      const err = data?.error
      if (err) {
        showMsg(typeof err === 'string' ? err : (err.message || 'Erro ao enviar teste.'), 'error')
        return
      }
      if (data?.test && data?.message) {
        showMsg(data.message, 'success')
      } else {
        showMsg('E-mail de teste enviado. Verifique a caixa do remetente.', 'success')
      }
    } catch (err) {
      console.error('Erro ao testar envio:', err)
      showMsg('Erro ao testar envio: ' + (err?.message || err), 'error')
    } finally {
      setConfigEmailTesting(false)
    }
  }

  const showMsg = (text, type = 'success') => {
    setMessage({ show: true, text, type })
    setTimeout(() => setMessage({ show: false, text: '', type: '' }), 4000)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const usuariosDisponiveis = useMemo(() => {
    const q = usuariosSearch.trim().toLowerCase()
    return usuarios
      .filter(u => u.id !== userId && !formData.usuarios_ids.includes(u.id))
      .filter(u => !q || (u.nome || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
  }, [usuarios, userId, formData.usuarios_ids, usuariosSearch])

  const addUsuario = (id) => {
    setFormData(prev => ({ ...prev, usuarios_ids: [...prev.usuarios_ids, id] }))
    setUsuariosSearch('')
  }

  const removeUsuario = (id) => {
    setFormData(prev => ({ ...prev, usuarios_ids: prev.usuarios_ids.filter(x => x !== id) }))
  }

  const verificarConflito = async (dataStr, horaStr, excludeEventoId = null) => {
    if (!dataStr || !horaStr) return false
    const dataHora = new Date(`${dataStr}T${horaStr}:00`)
    const start = new Date(dataHora)
    start.setSeconds(0, 0)
    const end = new Date(dataHora)
    end.setSeconds(59, 999)
    let q = supabase
      .from('eventos_calendario')
      .select('id')
      .gte('data_hora', start.toISOString())
      .lte('data_hora', end.toISOString())
    if (excludeEventoId) q = q.neq('id', excludeEventoId)
    const { data, error } = await q
    if (error) throw error
    return (data && data.length > 0)
  }

  const handleExcluirEvento = async () => {
    if (!eventoToExcluir) return
    try {
      setLoading(true)
      await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: eventoToExcluir.id, op: 'delete' })
      const { error } = await supabase
        .from('eventos_calendario')
        .delete()
        .eq('id', eventoToExcluir.id)
      if (error) throw error
      setEventoToExcluir(null)
      setShowExcluirModal(false)
      setShowDetalhesModal(false)
      loadEventos()
      showMsg('Evento excluído com sucesso!', 'success')
    } catch (err) {
      console.error('Erro ao excluir evento:', err)
      showMsg('Erro ao excluir evento: ' + (err.message || err), 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.nome_evento.trim()) {
      showMsg('Nome do evento é obrigatório', 'error')
      return
    }
    if (!formData.data || !formData.hora) {
      showMsg('Data e hora são obrigatórios', 'error')
      return
    }
    const isEdit = !!editingEventoId
    try {
      setLoading(true)
      const conflito = await verificarConflito(formData.data, formData.hora, isEdit ? editingEventoId : null)
      if (conflito) {
        setLoading(false)
        setShowConflitoModal(true)
        return
      }
      const dataHora = new Date(`${formData.data}T${formData.hora}:00`).toISOString()
      const antecip = formData.notificar_antecipacao_minutos ? parseInt(formData.notificar_antecipacao_minutos, 10) : null

      if (isEdit) {
        const { error: errUpd } = await supabase
          .from('eventos_calendario')
          .update({
            nome_evento: formData.nome_evento.trim(),
            tipo_evento: formData.tipo_evento || 'vendas',
            data_hora: dataHora,
            observacao: formData.observacao.trim() || null,
            notificar_antecipacao_minutos: antecip
          })
          .eq('id', editingEventoId)
        if (errUpd) throw errUpd
        const { error: errDel } = await supabase
          .from('eventos_calendario_usuarios')
          .delete()
          .eq('evento_id', editingEventoId)
        if (errDel) throw errDel
        if (formData.usuarios_ids.length > 0) {
          const rows = formData.usuarios_ids.map(uid => ({ evento_id: editingEventoId, usuario_id: uid }))
          const { error: errU } = await supabase.from('eventos_calendario_usuarios').insert(rows)
          if (errU) throw errU
        }
        await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: editingEventoId, op: 'upsert' })
        showMsg('Evento atualizado com sucesso!', 'success')
      } else {
        const { data: ev, error: errEv } = await supabase
          .from('eventos_calendario')
          .insert([{
            nome_evento: formData.nome_evento.trim(),
            tipo_evento: formData.tipo_evento || 'vendas',
            data_hora: dataHora,
            id_responsavel: userId,
            observacao: formData.observacao.trim() || null,
            notificar_antecipacao_minutos: antecip
          }])
          .select('id')
          .single()
        if (errEv) throw errEv
        if (formData.usuarios_ids.length > 0) {
          const rows = formData.usuarios_ids.map(uid => ({ evento_id: ev.id, usuario_id: uid }))
          const { error: errU } = await supabase.from('eventos_calendario_usuarios').insert(rows)
          if (errU) throw errU
        }
        await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: ev.id, op: 'upsert' })
        showMsg('Evento agendado com sucesso!', 'success')
      }

      setFormData({ nome_evento: '', tipo_evento: 'vendas', data: '', hora: '', observacao: '', usuarios_ids: [], notificar_antecipacao_minutos: '' })
      setUsuariosSearch('')
      setUsuariosSearchFocused(false)
      setEditingEventoId(null)
      setFormResponsavelNome(null)
      setShowFormModal(false)
      loadEventos()
    } catch (err) {
      console.error(isEdit ? 'Erro ao atualizar evento:' : 'Erro ao agendar:', err)
      showMsg((isEdit ? 'Erro ao atualizar evento: ' : 'Erro ao agendar evento: ') + (err.message || err), 'error')
    } finally {
      setLoading(false)
    }
  }

  const marcarEventoRealizado = async (ev, valor) => {
    // Apenas eventos de vendas podem ser marcados manualmente, e somente pelo responsável.
    if ((ev.tipo_evento || 'vendas') !== 'vendas') {
      showMsg('Eventos operacionais são marcados automaticamente na baixa da ordem de serviço.', 'error')
      return
    }
    if (String(ev.id_responsavel) !== String(userId)) {
      showMsg('Apenas o responsável pode marcar este evento como realizado.', 'error')
      return
    }
    try {
      const { error } = await supabase
        .from('eventos_calendario')
        .update({ executado: valor })
        .eq('id', ev.id)
      if (error) throw error
      showMsg(valor ? 'Evento marcado como realizado!' : 'Marcação removida.', 'success')
      loadEventos()
    } catch (err) {
      console.error('Erro ao atualizar evento:', err)
      showMsg('Erro ao atualizar evento: ' + (err.message || err), 'error')
    }
  }

  const formatHora = (iso) => {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  const formatData = (d) => {
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const getEventoTooltip = (ev) => {
    const resp = ev.responsavel?.nome || '—'
    const us = (ev.eventos_calendario_usuarios || []).map(x => x.usuarios_sistema?.nome).filter(Boolean)
    let t = `${formatHora(ev.data_hora)} - ${ev.nome_evento}\nCadastrado por: ${resp}`
    if (us.length) t += `\nAssociados: ${us.join(', ')}`
    if (ev.observacao) t += `\nObs: ${ev.observacao}`
    return t
  }

  const openDetalhes = (dia) => {
    setDetalhesDia(dia)
    setShowDetalhesModal(true)
  }

  const openNovoEvento = () => {
    setEditingEventoId(null)
    setFormResponsavelNome(null)
    setFormData({ nome_evento: '', tipo_evento: 'vendas', data: '', hora: '', observacao: '', usuarios_ids: [], notificar_antecipacao_minutos: '' })
    setUsuariosSearch('')
    setUsuariosSearchFocused(false)
    setShowFormModal(true)
  }

  const openEdit = (ev) => {
    const d = new Date(ev.data_hora)
    const dataStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const horaStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    const usuarioIds = (ev.eventos_calendario_usuarios || []).map(x => x.usuario_id).filter(Boolean)
    setFormData({
      nome_evento: ev.nome_evento || '',
      tipo_evento: ev.tipo_evento || 'vendas',
      data: dataStr,
      hora: horaStr,
      observacao: ev.observacao || '',
      usuarios_ids: usuarioIds,
      notificar_antecipacao_minutos: ev.notificar_antecipacao_minutos != null ? String(ev.notificar_antecipacao_minutos) : ''
    })
    setUsuariosSearch('')
    setUsuariosSearchFocused(false)
    setFormResponsavelNome(ev.responsavel?.nome || null)
    setEditingEventoId(ev.id)
    setShowDetalhesModal(false)
    setShowFormModal(true)
  }

  const closeFormModal = () => {
    setShowFormModal(false)
    setEditingEventoId(null)
    setFormResponsavelNome(null)
  }

  const goPrev = () => {
    const d = new Date(agendaDate)
    if (viewMode === 'dia') d.setDate(d.getDate() - 1)
    else if (viewMode === 'semana') d.setDate(d.getDate() - 7)
    else d.setMonth(d.getMonth() - 1)
    setAgendaDate(d)
  }

  const goNext = () => {
    const d = new Date(agendaDate)
    if (viewMode === 'dia') d.setDate(d.getDate() + 1)
    else if (viewMode === 'semana') d.setDate(d.getDate() + 7)
    else d.setMonth(d.getMonth() + 1)
    setAgendaDate(d)
  }

  const goToday = () => {
    setAgendaDate(new Date())
  }

  const { rangeStart, rangeEnd, title } = useMemo(() => {
    const d = new Date(agendaDate)
    const fmt = (x) => x.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    if (viewMode === 'dia') {
      const s = startOfDay(d)
      const e = endOfDay(d)
      const tit = d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      return { rangeStart: s, rangeEnd: e, title: tit.charAt(0).toUpperCase() + tit.slice(1) }
    }
    if (viewMode === 'semana') {
      const s = startOfWeek(d)
      const e = endOfWeek(d)
      return { rangeStart: s, rangeEnd: e, title: `${fmt(s)} – ${fmt(e)}` }
    }
    const s = startOfMonth(d)
    const e = endOfMonth(d)
    return {
      rangeStart: s,
      rangeEnd: e,
      title: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`
    }
  }, [agendaDate, viewMode])

  const eventosNoPeriodo = useMemo(() => {
    return eventos.filter(ev => eventInRange(ev, rangeStart, rangeEnd))
  }, [eventos, rangeStart, rangeEnd])

  const eventosPorDia = useMemo(() => {
    const map = {}
    eventosNoPeriodo.forEach(ev => {
      const key = new Date(ev.data_hora).toLocaleDateString('pt-BR')
      if (!map[key]) map[key] = []
      map[key].push(ev)
    })
    Object.keys(map).forEach(k => map[k].sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora)))
    return map
  }, [eventosNoPeriodo])

  const detalhesEventos = useMemo(() => {
    if (!detalhesDia) return []
    const key = new Date(detalhesDia).toLocaleDateString('pt-BR')
    return eventosPorDia[key] || []
  }, [detalhesDia, eventosPorDia])

  const responsavelNome = (formResponsavelNome != null ? formResponsavelNome : (userName || 'Você'))

  const isEventoOs = (ev) => {
    const nome = String(ev.nome_evento || '')
    return (ev.tipo_evento || 'vendas') === 'operacional' || /^OS\s*#/i.test(nome) || /instala/i.test(nome)
  }

  const tagEvento = (ev) => {
    if (isEventoOs(ev)) {
      if (/instala/i.test(String(ev.nome_evento || ''))) return 'Instalação'
      return 'Operacional'
    }
    return 'Vendas'
  }

  const renderEventoCard = (ev, { compact = false, diaClick } = {}) => (
    <div
      key={ev.id}
      className={`cal-event-card ${isEventoOs(ev) ? 'cal-event-os' : 'cal-event-pessoa'}${compact ? ' is-compact' : ''}`}
      title={getEventoTooltip(ev)}
      onClick={(e) => {
        e.stopPropagation()
        openDetalhes(diaClick || new Date(ev.data_hora))
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openDetalhes(diaClick || new Date(ev.data_hora))
        }
      }}
    >
      <div className="cal-event-card-main">
        <i className={`bi ${isEventoOs(ev) ? 'bi-tools' : 'bi-person-fill'}`} aria-hidden="true" />
        <span className="cal-event-time">{formatHora(ev.data_hora)}</span>
        <span className="cal-event-name">{ev.nome_evento}</span>
      </div>
      {!compact && <span className="cal-event-tag">{tagEvento(ev)}</span>}
    </div>
  )

  const renderVistaDia = () => {
    const key = agendaDate.toLocaleDateString('pt-BR')
    const list = eventosPorDia[key] || []
    const horaAtual = new Date()
    const mesmoDia = agendaDate.toDateString() === horaAtual.toDateString()
    const horasBase = Array.from({ length: 15 }, (_, i) => i + 6)
    const horasExtra = list.map(ev => new Date(ev.data_hora).getHours())
    const horas = Array.from(new Set([...horasBase, ...horasExtra])).sort((a, b) => a - b)
    return (
      <div className="cal-vista-dia">
        {horas.map(h => {
          const eventosHora = list.filter(ev => new Date(ev.data_hora).getHours() === h)
          const isNow = mesmoDia && horaAtual.getHours() === h
          return (
            <div key={h} className={`cal-time-slot${isNow ? ' is-now' : ''}${eventosHora.length ? ' has-events' : ''}`}>
              <div className="cal-time-label">{String(h).padStart(2, '0')}:00</div>
              <div className="cal-time-content">
                {eventosHora.map(ev => renderEventoCard(ev))}
              </div>
            </div>
          )
        })}
        {list.length === 0 && !loading && (
          <p className="agenda-empty">Nenhum evento neste dia.</p>
        )}
      </div>
    )
  }

  const renderVistaSemana = () => {
    const days = []
    const cur = new Date(rangeStart)
    while (cur <= rangeEnd) {
      days.push(new Date(cur))
      cur.setDate(cur.getDate() + 1)
    }
    return (
      <div className="cal-vista-semana">
        <div className="cal-semana-grid">
          {days.map(d => {
            const key = d.toLocaleDateString('pt-BR')
            const list = eventosPorDia[key] || []
            const isToday = d.toDateString() === new Date().toDateString()
            return (
              <div
                key={key}
                className={`cal-semana-dia ${isToday ? 'hoje' : ''} ${list.length ? 'has-events' : ''}`}
                onClick={() => openDetalhes(d)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetalhes(d) } }}
              >
                <div className="cal-semana-dia-header">
                  <span className="cal-semana-wday">{WEEKDAY_LABELS[d.getDay()]}</span>
                  <span className="cal-semana-num">{d.getDate()}</span>
                </div>
                <div className="cal-semana-events">
                  {list.map(ev => renderEventoCard(ev, { compact: true, diaClick: d }))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderVistaMensal = () => {
    const first = new Date(rangeStart)
    const last = new Date(rangeEnd)
    const startPad = first.getDay()
    const totalDays = last.getDate()
    const cells = []
    for (let i = 0; i < startPad; i++) cells.push({ empty: true })
    for (let d = 1; d <= totalDays; d++) {
      const dte = new Date(first.getFullYear(), first.getMonth(), d)
      const key = dte.toLocaleDateString('pt-BR')
      const list = eventosPorDia[key] || []
      const isToday = dte.toDateString() === new Date().toDateString()
      cells.push({ empty: false, date: dte, key, list, isToday })
    }
    return (
      <div className="cal-vista-mensal">
        <div className="cal-mensal-weekdays">
          {WEEKDAY_LABELS.map(l => (
            <div key={l} className="cal-mensal-wday">{l}</div>
          ))}
        </div>
        <div className="cal-mensal-grid">
          {cells.map((c, i) => (
            <div
              key={i}
              className={`cal-mensal-cell ${c.empty ? 'empty' : ''} ${c.isToday ? 'hoje' : ''} ${c.list?.length ? 'has-events' : ''}`}
              onClick={!c.empty ? () => openDetalhes(c.date) : undefined}
              role={!c.empty ? 'button' : undefined}
              tabIndex={!c.empty ? 0 : undefined}
              onKeyDown={!c.empty ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetalhes(c.date) } } : undefined}
            >
              {!c.empty && (
                <>
                  <div className="cal-mensal-num">{c.date.getDate()}</div>
                  <div className="cal-mensal-events">
                    {c.list.slice(0, 3).map(ev => renderEventoCard(ev, { compact: true, diaClick: c.date }))}
                    {c.list.length > 3 && (
                      <div className="cal-mensal-more">+{c.list.length - 3} mais</div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="calendario-container">
      {message.show && (
        <div className={`calendario-alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="cal-card">
        <div className="cal-card-header">
          <div className="cal-header-brand">
            <i className="bi bi-calendar3 cal-header-icon" aria-hidden="true" />
            <h1 className="cal-header-title">Calendário</h1>
            {googleAgenda.conectado && (
              <span className="cal-badge-google" title={googleAgenda.google_email || 'Google Agenda conectada'}>
                Google
              </span>
            )}
            <div className="cal-view-toggle" role="group" aria-label="Visualização">
              <button
                type="button"
                className={`cal-toggle-btn ${viewMode === 'dia' ? 'is-active' : ''}`}
                onClick={() => setViewMode('dia')}
              >
                Dia
              </button>
              <button
                type="button"
                className={`cal-toggle-btn ${viewMode === 'semana' ? 'is-active' : ''}`}
                onClick={() => setViewMode('semana')}
              >
                Semana
              </button>
              <button
                type="button"
                className={`cal-toggle-btn ${viewMode === 'mensal' ? 'is-active' : ''}`}
                onClick={() => setViewMode('mensal')}
              >
                Mensal
              </button>
            </div>
          </div>

          <div className="cal-header-nav">
            <button type="button" className="cal-nav-arrow" onClick={goPrev} aria-label="Anterior">
              <i className="bi bi-chevron-left" aria-hidden="true" />
            </button>
            <div className="cal-nav-date">
              <h2 className="cal-nav-title">{title}</h2>
              {viewMode === 'dia' && (
                <button type="button" className="cal-link-detalhes" onClick={() => openDetalhes(agendaDate)}>
                  Ver detalhes do dia
                </button>
              )}
            </div>
            <button type="button" className="cal-nav-arrow" onClick={goNext} aria-label="Próximo">
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
            <button type="button" className="cal-nav-hoje" onClick={goToday}>
              Hoje
            </button>
          </div>

          <div className="cal-header-actions">
            <button type="button" className="cal-btn-novo" onClick={openNovoEvento}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Novo evento
            </button>
            <div className="calendario-menu-wrap">
              <button
                type="button"
                className="calendario-menu-trigger"
                onClick={() => setShowMenuCalendario(!showMenuCalendario)}
                aria-label="Menu"
                aria-expanded={showMenuCalendario}
              >
                ⋮
              </button>
              {showMenuCalendario && (
                <>
                  <div
                    className="calendario-menu-backdrop"
                    onClick={() => setShowMenuCalendario(false)}
                    aria-hidden="true"
                  />
                  <div className="calendario-menu-dropdown">
                    {onBack && (
                      <button
                        type="button"
                        className="calendario-menu-item"
                        onClick={() => {
                          setShowMenuCalendario(false)
                          onBack()
                        }}
                      >
                        ← Voltar
                      </button>
                    )}
                    {googleAgenda.conectado ? (
                      <>
                        <span className="calendario-menu-item calendario-menu-hint">
                          Google: {googleAgenda.google_email || 'conectado'}
                        </span>
                        <button
                          type="button"
                          className="calendario-menu-item"
                          onClick={async () => {
                            setShowMenuCalendario(false)
                            const res = await desconectarGoogleCalendar(userId)
                            if (res.success) {
                              setGoogleAgenda({ conectado: false, google_email: null, loading: false })
                              showMsg('Google Agenda desconectada.', 'success')
                            } else {
                              showMsg(res.error || 'Não foi possível desconectar.', 'error')
                            }
                          }}
                        >
                          Desconectar Google Agenda
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="calendario-menu-item"
                        onClick={async () => {
                          setShowMenuCalendario(false)
                          const res = await iniciarConexaoGoogleCalendar(userId)
                          if (!res.success) showMsg(res.error || 'Não foi possível conectar.', 'error')
                        }}
                      >
                        Conectar Google Agenda
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="cal-card-body">
          {loading && !showFormModal ? (
            <p className="calendario-loading">Carregando...</p>
          ) : viewMode === 'dia' ? (
            renderVistaDia()
          ) : viewMode === 'semana' ? (
            renderVistaSemana()
          ) : (
            renderVistaMensal()
          )}
        </div>
      </div>

      {/* Modal Novo Evento / Editar Evento */}
      {showFormModal && (
        <div className="calendario-modal-overlay" onClick={closeFormModal}>
          <div className="calendario-modal calendario-modal-form" onClick={e => e.stopPropagation()}>
            <div className="calendario-modal-header">
              <h3>{editingEventoId ? 'Editar evento' : 'Agendar evento'}</h3>
              <button type="button" className="calendario-modal-close" onClick={closeFormModal} aria-label="Fechar">
                ×
              </button>
            </div>
            <form className="calendario-form" onSubmit={handleSubmit}>
              <div className="calendario-modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="nome_evento">Nome do evento <span className="required">*</span></label>
                    <input
                      id="nome_evento"
                      name="nome_evento"
                      type="text"
                      value={formData.nome_evento}
                      onChange={handleInputChange}
                      placeholder="Ex: Reunião de equipe"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tipo_evento">Tipo do evento <span className="required">*</span></label>
                    <select
                      id="tipo_evento"
                      name="tipo_evento"
                      value={formData.tipo_evento}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="vendas">Vendas</option>
                      <option value="operacional">Operacional</option>
                    </select>
                  </div>
                </div>
                <div className="form-row dois-cols">
                  <div className="form-group">
                    <label htmlFor="data">Data <span className="required">*</span></label>
                    <input
                      id="data"
                      name="data"
                      type="date"
                      value={formData.data}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="hora">Hora <span className="required">*</span></label>
                    <input
                      id="hora"
                      name="hora"
                      type="time"
                      value={formData.hora}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Responsável por cadastrar</label>
                  <input type="text" value={responsavelNome} readOnly className="readonly" />
                </div>
                <div className="form-group">
                  <label>Usuários associados ao evento</label>
                  <span className="form-hint">
                    O compromisso entra na Google Agenda de cada pessoa associada que conectou a conta.
                    Quem ainda não conectou recebe convite no e-mail do cadastro.
                  </span>
                  <div className="usuarios-searchbox">
                    {formData.usuarios_ids.length > 0 && (
                      <div className="usuarios-chips">
                        {formData.usuarios_ids.map(uid => {
                          const u = usuarios.find(x => x.id === uid)
                          return u ? (
                            <span key={u.id} className="usuarios-chip">
                              {u.nome}
                              <button type="button" className="usuarios-chip-remove" onClick={() => removeUsuario(u.id)} aria-label="Remover">×</button>
                            </span>
                          ) : null
                        })}
                      </div>
                    )}
                    <div className="usuarios-search-wrap">
                      <input
                        type="text"
                        value={usuariosSearch}
                        onChange={e => setUsuariosSearch(e.target.value)}
                        onFocus={() => setUsuariosSearchFocused(true)}
                        onBlur={() => setTimeout(() => setUsuariosSearchFocused(false), 150)}
                        placeholder="Buscar usuários para associar..."
                        className="usuarios-search-input"
                        autoComplete="off"
                      />
                      {usuariosSearchFocused && (
                        <div className="usuarios-dropdown">
                          {usuariosDisponiveis.length === 0 ? (
                            <div className="usuarios-dropdown-empty">
                              {usuariosSearch.trim()
                                ? 'Nenhum usuário encontrado.'
                                : (usuarios.filter(u => u.id !== userId).length === 0
                                  ? 'Nenhum outro usuário disponível.'
                                  : 'Todos os usuários já foram adicionados.')}
                            </div>
                          ) : (
                            usuariosDisponiveis.map(u => (
                              <button
                                key={u.id}
                                type="button"
                                className="usuarios-dropdown-item"
                                onMouseDown={(e) => { e.preventDefault(); addUsuario(u.id) }}
                              >
                                <span className="usuarios-dropdown-nome">{u.nome}</span>
                                {u.email && <span className="usuarios-dropdown-email">{u.email}</span>}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="observacao">Observação</label>
                  <textarea
                    id="observacao"
                    name="observacao"
                    value={formData.observacao}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Observações adicionais (opcional)"
                  />
                </div>
              </div>
              <div className="calendario-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (editingEventoId ? 'Salvando...' : 'Agendando...') : (editingEventoId ? 'Salvar' : 'Agendar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Detalhes do Dia */}
      {showDetalhesModal && detalhesDia && (
        <div className="calendario-modal-overlay" onClick={() => setShowDetalhesModal(false)}>
          <div className="calendario-modal calendario-modal-detalhes" onClick={e => e.stopPropagation()}>
            <div className="calendario-modal-header">
              <h3>Agendamentos – {formatData(detalhesDia)}</h3>
              <button type="button" className="calendario-modal-close" onClick={() => setShowDetalhesModal(false)} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="calendario-modal-body">
              {detalhesEventos.length === 0 ? (
                <p className="agenda-empty">Nenhum evento neste dia.</p>
              ) : (
                <ul className="calendario-detalhes-list">
                  {detalhesEventos.map(ev => {
                    const resp = ev.responsavel?.nome || '—'
                    const us = (ev.eventos_calendario_usuarios || []).map(x => x.usuarios_sistema?.nome).filter(Boolean)
                    return (
                      <li key={ev.id} className="calendario-detalhes-item">
                        <div className="calendario-detalhes-hora">{formatHora(ev.data_hora)}</div>
                        <div className="calendario-detalhes-body">
                          <strong>{ev.nome_evento}</strong>
                          <div className="calendario-detalhes-meta">
                            <span>Tipo: {(ev.tipo_evento || 'vendas') === 'operacional' ? 'Operacional' : 'Vendas'}</span>
                            <span>Cadastrado por: {resp}</span>
                            {us.length > 0 && <span>Associados: {us.join(', ')}</span>}
                            {ev.observacao && <span className="obs">Obs: {ev.observacao}</span>}
                            <span style={{ fontWeight: 600, color: ev.executado ? '#198754' : '#6c757d' }}>
                              {ev.executado ? '✅ Realizado' : '⏳ Não realizado'}
                            </span>
                          </div>
                        </div>
                        <div className="calendario-detalhes-actions">
                          {(ev.tipo_evento || 'vendas') === 'vendas' && String(ev.id_responsavel) === String(userId) && (
                            ev.executado ? (
                              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => marcarEventoRealizado(ev, false)}>
                                Desmarcar
                              </button>
                            ) : (
                              <button type="button" className="btn btn-sm btn-success" onClick={() => marcarEventoRealizado(ev, true)}>
                                Marcar realizado
                              </button>
                            )
                          )}
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openEdit(ev)}>
                            Editar
                          </button>
                          {userRole === 'administrador' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                setEventoToExcluir(ev)
                                setShowExcluirModal(true)
                              }}
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className="calendario-modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setShowDetalhesModal(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Config. e-mail (remetente único das notificações) */}
      {showConfigEmail && (
        <div className="calendario-modal-overlay" onClick={() => setShowConfigEmail(false)}>
          <div className="calendario-modal calendario-modal-detalhes" onClick={e => e.stopPropagation()}>
            <div className="calendario-modal-header">
              <h3>E-mail para notificações</h3>
              <button type="button" className="calendario-modal-close" onClick={() => setShowConfigEmail(false)} aria-label="Fechar">
                ×
              </button>
            </div>
            <form className="calendario-form" onSubmit={saveConfigEmail}>
              <div className="calendario-modal-body">
                <p className="form-hint" style={{ marginBottom: 16 }}>
                  Um único e-mail do sistema envia lembretes (Gmail API). Use o mesmo Gmail configurado no OAuth.
                </p>
                <div className="form-group">
                  <label htmlFor="config_email_remetente">E-mail remetente (Gmail) <span className="required">*</span></label>
                  <input
                    id="config_email_remetente"
                    type="email"
                    value={configEmail.email_remetente}
                    onChange={e => setConfigEmail(prev => ({ ...prev, email_remetente: e.target.value }))}
                    placeholder="ex: notificacoes@gmail.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="config_nome_remetente">Nome do remetente</label>
                  <input
                    id="config_nome_remetente"
                    type="text"
                    value={configEmail.nome_remetente}
                    onChange={e => setConfigEmail(prev => ({ ...prev, nome_remetente: e.target.value }))}
                    placeholder="Sistema Calendário"
                  />
                </div>
                <p className="form-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                  Salve a configuração antes de testar. O e-mail de teste é enviado no mesmo instante para o remetente.
                </p>
              </div>
              <div className="calendario-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfigEmail(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={testarEnvioEmail}
                  disabled={configEmailSaving || configEmailTesting}
                >
                  {configEmailTesting ? 'Enviando...' : 'Testar envio'}
                </button>
                <button type="submit" className="btn btn-primary" disabled={configEmailSaving || configEmailTesting}>
                  {configEmailSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showConflitoModal}
        type="warning"
        title="Data/hora indisponível"
        message="Já existe um evento agendado para esta data e horário. Por favor, escolha outra data ou horário."
        confirmText="Entendi"
        cancelText="Fechar"
        onConfirm={() => setShowConflitoModal(false)}
        onCancel={() => setShowConflitoModal(false)}
      />

      <ConfirmModal
        show={showExcluirModal}
        type="danger"
        title="Excluir evento"
        message={eventoToExcluir ? `Deseja realmente excluir o evento "${eventoToExcluir.nome_evento}"?` : ''}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleExcluirEvento}
        onCancel={() => {
          setShowExcluirModal(false)
          setEventoToExcluir(null)
        }}
      />
    </div>
  )
}

export default Calendario
